// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Crew Backstory Integration
 * 
 * Handles backstory assignment and revelation for existing crew system
 */

import { assignBackstory, checkBackstoryUnlock, revealBackstory } from '../../data/crewBackstories.js';
import CrewManager from './CrewManager.js';
// eslint-disable-next-line no-unused-vars
import CrewRelationshipManager from './CrewRelationshipManager.js';

/**
 * Initialize backstories for existing crew members
 */
export function initializeBackstories() {
  const crew = CrewManager.getAllCrew();
  let updated = false;
  
  crew.forEach(crewMember => {
    if (!crewMember.backstory) {
      crewMember.backstory = assignBackstory(crewMember);
      updated = true;
    }
  });
  
  if (updated) {
    CrewManager.saveToStorage();
  }
}

/**
 * Check if any backstories should be revealed based on relationships
 */
export function checkBackstoryReveals() {
  const crew = CrewManager.getAllCrew();
  const reveals = [];
  
  crew.forEach(crewMember => {
    if (crewMember.backstory && !crewMember.backstory.revealed) {
      // For now, use a simple relationship level based on time served and interactions
      // In a full implementation, this would check actual relationship scores
      const relationshipLevel = Math.min(80, 30 + (Date.now() - crewMember.hiredAt) / (1000 * 60 * 60 * 24) * 2); // 2 points per day
      
      const unlock = checkBackstoryUnlock(crewMember, relationshipLevel);
      if (unlock) {
        revealBackstory(crewMember);
        reveals.push(unlock);
      }
    }
  });
  
  if (reveals.length > 0) {
    CrewManager.saveToStorage();
  }
  
  return reveals;
}

/**
 * Get crew member's backstory information for display
 */
export function getCrewBackstoryInfo(crewId) {
  const crewMember = CrewManager.getCrewMember(crewId);
  if (!crewMember || !crewMember.backstory) {
    return null;
  }
  
  return {
    title: crewMember.backstory.title,
    summary: crewMember.backstory.summary,
    details: crewMember.backstory.revealed ? crewMember.backstory.details : null,
    revealed: crewMember.backstory.revealed,
    unlockThreshold: crewMember.backstory.unlockThreshold,
    revealedAt: crewMember.backstory.revealedAt
  };
}

/**
 * Manually reveal a backstory (for testing or special events)
 */
export function forceRevealBackstory(crewId) {
  const crewMember = CrewManager.getCrewMember(crewId);
  if (crewMember && crewMember.backstory && !crewMember.backstory.revealed) {
    revealBackstory(crewMember);
    CrewManager.saveToStorage();
    return true;
  }
  return false;
}